/**
 * Shared reading of a Yandex geocoder result.
 *
 * Both the address suggester and the map picker used to interpret geocoder
 * responses themselves, and they disagreed: the picker took the *last*
 * administrative area as the city fallback, the suggester the first. They also
 * threw away everything except the locality name, which is why a picked
 * address never carried coordinates.
 *
 * Probed against the live API before writing this — the geocoder answers
 * almost any string, including nonsense:
 *
 *   "Москва, проспект Мира, 114" → kind=house,    precision=exact
 *   "Москва, Зубовский бульвар"  → kind=street,   precision=street
 *   "Москва"                     → kind=locality, precision=other
 *   "дом у реки"                 → kind=locality, precision=other
 *                                  ("СНТ Дом у реки", Кемеровская обл.)
 *
 * So the presence of coordinates proves nothing on its own — `kind`/
 * `precision` are the only things separating a real address from a plausible
 * misreading, and every caller must go through `isPreciseEnough`.
 */

/** Minimal shape of a geocoder geoObject; @types/yandex-maps declares none of it. */
export interface GeoObjectLike {
	getLocalities?:          () => string[]
	getAdministrativeAreas?: () => string[]
	getAddressLine?:         () => string
	geometry?:               { getCoordinates?: () => number[] } | null
	properties:              { get: (path: string) => unknown }
}

export interface ResolvedAddress {
	address:   string
	city?:     string
	latitude:  number
	longitude: number
	kind?:     string
	precision?: string
}

const KIND_PATH      = 'metaDataProperty.GeocoderMetaData.kind'
const PRECISION_PATH = 'metaDataProperty.GeocoderMetaData.precision'

const asString = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined)

/** Yandex prefixes full address lines with the country; nobody wants to see it. */
function stripCountry(line: string): string {
	return line.replace(/^Россия,\s*/i, '').trim()
}

export function resolveGeoObject(geoObject: GeoObjectLike | undefined | null): ResolvedAddress | null {
	if (!geoObject) return null

	const coords = geoObject.geometry?.getCoordinates?.()
	if (!coords || coords.length < 2) return null
	const [latitude, longitude] = coords
	if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null

	const localities  = geoObject.getLocalities?.() ?? []
	const adminAreas  = geoObject.getAdministrativeAreas?.() ?? []
	// The narrowest administrative area is the closest thing to a city when the
	// geocoder reports no locality — the map picker's reading, kept over the
	// suggester's, which took the widest.
	const city = localities[0] ?? adminAreas[adminAreas.length - 1]

	const line = geoObject.getAddressLine?.()

	return {
		address:   line ? stripCountry(line) : '',
		city,
		latitude,
		longitude,
		kind:      asString(geoObject.properties.get(KIND_PATH)),
		precision: asString(geoObject.properties.get(PRECISION_PATH)),
	}
}

/**
 * Whether a result identifies a building rather than a street, district or
 * settlement. Land plots are allowed to stop at street level — a plot often
 * has no house number at all.
 */
export function isPreciseEnough(r: Pick<ResolvedAddress, 'kind' | 'precision'>, allowStreetLevel = false): boolean {
	if (r.kind === 'house' || r.precision === 'exact' || r.precision === 'number') return true
	return allowStreetLevel && (r.kind === 'street' || r.precision === 'street')
}
